import { useRef, useState, type ReactNode } from "react";

export type TableColumn = {
  key: string;
  label: string;
  /** Initial width in px. The user can drag-resize from there. */
  width: number;
  minWidth?: number;
};

/**
 * Excel/Google-Sheets-style table with drag-to-resize columns, per AGENTS.md §6.3 — this is
 * a hard UX requirement the business owner asked for explicitly, not a nice-to-have.
 * Implemented with table-layout:fixed + a <colgroup> driven by per-column width state, plus
 * a drag handle on each header cell.
 */
export function ResizableTable({
  columns,
  children,
}: {
  columns: TableColumn[];
  children: ReactNode;
}) {
  const [widths, setWidths] = useState<Record<string, number>>(() =>
    Object.fromEntries(columns.map((c) => [c.key, c.width])),
  );
  const dragState = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  function startResize(key: string, startWidth: number) {
    return (e: React.MouseEvent) => {
      e.preventDefault();
      dragState.current = { key, startX: e.clientX, startWidth };
      setActiveKey(key);

      function onMove(moveEvent: MouseEvent) {
        if (!dragState.current) return;
        const { key: dragKey, startX, startWidth: dragStartWidth } = dragState.current;
        const column = columns.find((c) => c.key === dragKey);
        const min = column?.minWidth ?? 60;
        const next = Math.max(min, dragStartWidth + (moveEvent.clientX - startX));
        setWidths((prev) => ({ ...prev, [dragKey]: next }));
      }

      function onUp() {
        dragState.current = null;
        setActiveKey(null);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      }

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };
  }

  return (
    <div className="orders-table-wrap">
      <table className="resizable-table">
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={{ width: widths[c.key] }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}>
                {c.label}
                <div
                  className={`resize-handle ${activeKey === c.key ? "active" : ""}`}
                  onMouseDown={startResize(c.key, widths[c.key])}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
